import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import type {
  ClientProductSummaryDto,
  ListClientProductsResponse,
  PlantEditPayload,
  PlantOverviewDto,
} from '../api/types';
import { useAsync } from '../hooks/useAsync';
import { GeoPickerModal } from './GeoPickerModal';
import { showToast } from './Toaster';
import { showConfirm } from './Confirmer';
import { ProductIcon } from '../pages/ClientCatalogPage';
import {
  ClientLinuxGateway,
  ClientAndroidPanel,
  ClientWindowsPc,
  ClientIosPanel,
  ClientEmbedded,
  IconPickOnMap,
  IconSave,
  IconCancel,
  IconDeleteHard,
} from '../icons/HvacIcons';
import './hv-modal.css';
import '../pages/PlantsPage.css';

/** Erweiterte Field-Client-Zeile — jetzt mit Product-Overlay + Instanz-
 *  Feldern, die die Anlagen-Übersicht in der Client-Column konsumieren
 *  will (Serial, Asset-Tag, Kauf-Datum, Garantie-Referenz). Alles optional,
 *  legacy `hardwareKind` bleibt als Fallback erhalten. */
interface FieldClientListItem {
  id: string;
  mqttClientName: string;
  displayName: string | null;
  hardwareKind: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  // Product-Overlay-Felder (2026-08-31)
  productId?: string | null;
  serialNumber?: string | null;
  assetTag?: string | null;
  purchaseDate?: string | null;
  warrantyUntil?: string | null;
  warrantyReference?: string | null;
  warrantyNotes?: string | null;
  purchaseInvoiceUrl?: string | null;
  purchaseInvoiceArtifactId?: string | null;
  deployedAt?: string | null;
}
interface ListClientsResponse { clients: FieldClientListItem[] }

/** ISO → yyyy-MM-dd for <input type="date">. Returns '' on null/invalid. */
function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}
/** Date-input → ISO. UTC noon to avoid TZ-off-by-one drift. */
function fromDateInput(v: string): string | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  return new Date(`${trimmed}T12:00:00Z`).toISOString();
}
/** Add `months` months onto a yyyy-MM-dd date-input value, returning the
 *  same yyyy-MM-dd shape. Empty string on any input problem so the caller
 *  can safely stuff the result straight into a date input. */
function addMonthsToDateInput(dateInput: string, months: number | null | undefined): string {
  if (!dateInput || !months || !Number.isFinite(months)) return '';
  const d = new Date(`${dateInput}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

interface Props {
  /** Full row from /api/plants/overview. Loaded into the form state; the
   *  parent gets the freshly-saved payload back so it can update its
   *  local list without a full refetch. */
  plant: PlantOverviewDto;
  onSaved(patch: PlantEditPayload): void;
  onDeleted(): void;
  onClose(): void;
}

/**
 * Full-plant edit dialog (added 2026-08-29). Replaces the legacy
 * PlantMetadataModal (name + address + coords + delete) with a broader
 * form that carries the structured address, primary contact, notes and
 * timezone. Every field is optional except <c>displayName</c>. The delete
 * path stays soft-delete + cascade (server-side), same as before.
 *
 * Design notes:
 *  - Two-column layout on wide viewports; falls back to single-column on
 *    modals under 600 px so the modal footprint stays predictable on
 *    tablets.
 *  - Empty-string on save is a signal to clear a previously-populated
 *    field (server treats "" as null). The legacy ClearFields opt-in is
 *    still supported by the endpoint but the UI no longer needs it.
 *  - No client-side email regex — the server does not validate it either,
 *    and typing an incomplete email during data entry should not block
 *    a partial save. `type="email"` gives the browser the right keyboard
 *    on mobile without hard-blocking submit.
 */
export function PlantEditModal({ plant, onSaved, onDeleted, onClose }: Props) {
  const [displayName, setDisplayName] = useState(plant.displayName);
  const [address, setAddress] = useState(plant.address ?? '');
  const [addressStreet, setAddressStreet] = useState(plant.addressStreet ?? '');
  const [addressCity, setAddressCity] = useState(plant.addressCity ?? '');
  const [addressZip, setAddressZip] = useState(plant.addressZip ?? '');
  const [addressCountry, setAddressCountry] = useState(plant.addressCountry ?? '');
  const [latitude, setLatitude] = useState(plant.latitude?.toString() ?? '');
  const [longitude, setLongitude] = useState(plant.longitude?.toString() ?? '');
  const [contactName, setContactName] = useState(plant.contactName ?? '');
  const [contactEmail, setContactEmail] = useState(plant.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(plant.contactPhone ?? '');
  const [notes, setNotes] = useState(plant.notes ?? '');
  const [timezone, setTimezone] = useState(plant.timezone ?? '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [geoPickerOpen, setGeoPickerOpen] = useState(false);
  // Seehöhe: initial vom Server (Backfill via ElevationBackfillHostedService),
  // wird bei GeoPicker-Pick optimistisch überschrieben. Der Server aktualisiert
  // sie ohnehin nach dem Save neu; das Feld ist reine Anzeige, kein Save-Input.
  const [elevationM, setElevationM] = useState<number | null>(plant.elevationM ?? null);

  // Field-Client-Liste + lokal gehaltene "gerade eingestellte" Werte.
  // Änderungen am Typ werden direkt PATCH-t (kein Sammel-Save) — die
  // Anlagen-Bearbeitung ist metadata-focused, der Client-Tag ist eine
  // separate Achse und soll die Speicher-Semantik nicht komplizieren.
  const [clientTick, setClientTick] = useState(0);
  const clientsState = useAsync(
    (signal) => api.get<ListClientsResponse>(`/api/plants/${plant.id}/clients`, signal),
    [plant.id, clientTick],
  );
  const clients = clientsState.status === 'ok' ? clientsState.data.clients : [];

  // Approved-only Produktliste für das Product-Picker-Dropdown. Nicht-Admins
  // bekommen ohnehin nur Approved+enabled+non-excluded zurück; wir gehen
  // durchgehend über diese Sicht (`status=approved`), damit der Client-
  // Katalog-Admin die Auswahl nicht mit Draft/Retired zumüllt.
  const productsState = useAsync(
    (signal) => api.get<ListClientProductsResponse>(
      '/api/client-catalog/products?status=approved',
      signal,
    ),
    [plant.id],
  );
  const products = productsState.status === 'ok' ? productsState.data.items : [];
  const productById = useMemo(() => {
    const m = new Map<string, ClientProductSummaryDto>();
    for (const p of products) m.set(p.id, p);
    return m;
  }, [products]);

  /** Single-field PATCH auf einen Field-Client. `clearFields` wird nicht
   *  automatisch erzeugt — Aufrufer, die eine nullable-Spalte aktiv leeren
   *  wollen, senden das Feld selbst mit null und listen es zusätzlich in
   *  `clearFields`. Für die typische "Blur speichert" Semantik reicht der
   *  einfache Null-durchreichen-Pfad. */
  const patchClient = async (
    clientId: string,
    body: Record<string, unknown>,
    toastTitle: string,
    toastMessage: string,
  ) => {
    try {
      await api.patch(`/api/plants/${plant.id}/clients/${clientId}`, body);
      setClientTick((n) => n + 1);
      showToast({
        tone: 'success',
        title: toastTitle,
        message: toastMessage,
        autoHideAfterMs: 2500,
      });
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      const body = err instanceof ApiError
        ? (err.body as { error?: string; detail?: string; title?: string } | undefined)
        : undefined;
      const serverMsg = body?.error ?? body?.detail ?? body?.title;
      const msg = err instanceof ApiError
        ? `HTTP ${status}${serverMsg ? ' — ' + serverMsg : ''}`
        : ((err as Error)?.message ?? 'Unbekannter Fehler');
      showToast({
        tone: 'error',
        title: `${toastTitle} fehlgeschlagen`,
        message: msg,
        autoHideAfterMs: 8000,
      });
      setError(msg);
    }
  };

  const reclaimClients = async () => {
    try {
      const res = await api.post<{ reclaimed: Array<{ mqttClientName: string; hostname: string }>; skipped: Array<{ reason: string }> }>(
        `/api/plants/${plant.id}/clients/reclaim`, {});
      setClientTick((n) => n + 1);
      if (res.reclaimed.length > 0) {
        showToast({
          tone: 'success',
          title: `${res.reclaimed.length} Client(s) rekonstruiert`,
          message: res.reclaimed.map((r) => `${r.hostname} (${r.mqttClientName})`).join(', '),
          autoHideAfterMs: 6000,
        });
      } else {
        showToast({
          tone: 'warn',
          title: 'Kein Match',
          message: `Keine Orphan-runtime-info konnte per Deterministic-ID-Match wieder zugeordnet werden. ${res.skipped.length} probiert.`,
          autoHideAfterMs: 8000,
        });
      }
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      const body = err instanceof ApiError
        ? (err.body as { error?: string; detail?: string; title?: string } | undefined)
        : undefined;
      const serverMsg = body?.error ?? body?.detail ?? body?.title;
      showToast({
        tone: 'error',
        title: 'Reclaim fehlgeschlagen',
        message: err instanceof ApiError
          ? `HTTP ${status}${serverMsg ? ' — ' + serverMsg : ''}`
          : ((err as Error)?.message ?? 'Unbekannter Fehler'),
        autoHideAfterMs: 8000,
      });
    }
  };

  const deleteClient = async (clientId: string, label: string) => {
    // Guard: wenn's der letzte FieldClient waere UND die Anlage bereits
    // Devices hat, warnen wir explizit vor Waisen — Devices bleiben ohne
    // meldenden Client stumm, und der User verliert die Zuordnung
    // (Serial, Garantie, Product-Tag). Wenn der Client-Runtime noch laeuft
    // legt der naechste Heartbeat automatisch einen neuen Record an, sonst
    // muss re-provisioniert werden.
    const isLastClient = clients.length === 1;
    const deviceHint = plant.clientCount > 0
      ? 'Diese Anlage hat bereits Devices die per Discovery erstellt wurden. Nach dem Loeschen bleiben sie ohne meldenden Client zurueck bis der Client-Runtime sich per Heartbeat neu registriert.'
      : '';
    const warning = isLastClient
      ? `„${label}" ist der EINZIGE FieldClient dieser Anlage. ${deviceHint} Fortfahren nur wenn du sicher bist dass der Client-Record ein Zombie ist ODER du gleich neu provisionierst.`
      : `„${label}" wird hart aus dem Katalog geloescht (inkl. Runtime-Info + Interfaces). Kein Undo.`;
    const ok = await showConfirm({
      title: isLastClient ? 'Letzten FieldClient entfernen?' : 'Client-Zeile entfernen?',
      message: warning,
      confirmLabel: 'Entfernen',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/api/plants/${plant.id}/clients/${clientId}`);
      setClientTick((n) => n + 1);
      showToast({ tone: 'success', title: 'Client entfernt', message: label, autoHideAfterMs: 3000 });
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      const body = err instanceof ApiError
        ? (err.body as { error?: string; detail?: string; title?: string } | undefined)
        : undefined;
      const serverMsg = body?.error ?? body?.detail ?? body?.title;
      showToast({
        tone: 'error',
        title: 'Client-Entfernen fehlgeschlagen',
        message: err instanceof ApiError
          ? `HTTP ${status}${serverMsg ? ' — ' + serverMsg : ''}`
          : ((err as Error)?.message ?? 'Unbekannter Fehler'),
        autoHideAfterMs: 8000,
      });
    }
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const dn = displayName.trim();
      if (dn.length === 0) throw new Error('Anzeigename darf nicht leer sein.');

      // Build the PATCH body. Strings are passed as-is (empty means clear).
      // Coords use the ClearFields opt-out because "" is not a number and
      // the endpoint expects an explicit null/omit signal.
      const body: Record<string, unknown> = {
        displayName: dn,
        address: address.trim(),
        addressStreet: addressStreet.trim(),
        addressCity: addressCity.trim(),
        addressZip: addressZip.trim(),
        addressCountry: addressCountry.trim(),
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim(),
        contactPhone: contactPhone.trim(),
        notes: notes.trim(),
        timezone: timezone.trim(),
      };
      const clear: string[] = [];
      const lat = latitude.trim();
      if (lat === '') clear.push('latitude');
      else {
        const n = Number(lat);
        if (!Number.isFinite(n)) throw new Error('Latitude ist keine gültige Zahl.');
        body.latitude = n;
      }
      const lon = longitude.trim();
      if (lon === '') clear.push('longitude');
      else {
        const n = Number(lon);
        if (!Number.isFinite(n)) throw new Error('Longitude ist keine gültige Zahl.');
        body.longitude = n;
      }
      // Elevation-Override: nur senden wenn wir eine Zahl haben. Der Server
      // akzeptiert einen double; ein String würde 500 werfen (400 wäre
      // richtiger, aber die Deserialisierung fällt schon vorher um).
      if (elevationM !== null && Number.isFinite(elevationM)) {
        body.elevationOverrideM = elevationM;
      } else if (plant.elevationM !== null && elevationM === null) {
        // Operator hat das Feld aktiv leergemacht → Override löschen.
        clear.push('elevationOverrideM');
      }
      if (clear.length > 0) body.clearFields = clear;

      const res = await api.patch<PlantEditPayload>(`/api/plants/${plant.id}`, body);
      onSaved(res);
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string })?.error;
        setError(bodyMsg ?? `Speichern fehlgeschlagen (${e.status}).`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler beim Speichern.');
      }
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/api/plants/${plant.id}`);
      onDeleted();
    } catch (e) {
      const msg = e instanceof ApiError ? `Löschen fehlgeschlagen (${e.status}).` : 'Unbekannter Fehler.';
      setError(msg);
      setSaving(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal hv-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Anlage bearbeiten</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-plants__form-grid">
            <div class="hv-modal__field hv-plants__form-grid--full">
              <label>Anzeigename</label>
              <input type="text" value={displayName}
                     onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)} />
            </div>
          </div>

          <div class="hv-plants__form-section">
            <h3 class="hv-plants__form-section-title">Adresse</h3>
            <div class="hv-plants__form-grid">
              <div class="hv-modal__field hv-plants__form-grid--full">
                <label>Straße + Hausnummer</label>
                <input type="text" value={addressStreet}
                       onInput={(e) => setAddressStreet((e.target as HTMLInputElement).value)}
                       placeholder="z.B. Musterstraße 12" />
              </div>
              <div class="hv-modal__field">
                <label>PLZ</label>
                <input type="text" value={addressZip}
                       onInput={(e) => setAddressZip((e.target as HTMLInputElement).value)} />
              </div>
              <div class="hv-modal__field">
                <label>Ort</label>
                <input type="text" value={addressCity}
                       onInput={(e) => setAddressCity((e.target as HTMLInputElement).value)} />
              </div>
              <div class="hv-modal__field">
                <label>Land</label>
                <input type="text" value={addressCountry}
                       onInput={(e) => setAddressCountry((e.target as HTMLInputElement).value)}
                       placeholder="z.B. AT oder Österreich" />
              </div>
              <div class="hv-modal__field">
                <label>Zeitzone</label>
                <input type="text" value={timezone}
                       onInput={(e) => setTimezone((e.target as HTMLInputElement).value)}
                       placeholder="z.B. Europe/Vienna" />
              </div>
              <div class="hv-modal__field hv-plants__form-grid--full">
                <label>Freitext-Adresse (optional, Legacy)</label>
                <input type="text" value={address}
                       onInput={(e) => setAddress((e.target as HTMLInputElement).value)}
                       placeholder="einzeilige Anschrift, wenn strukturiert nicht passt" />
              </div>
              <div class="hv-modal__field">
                <label>Latitude</label>
                <input type="text" value={latitude}
                       onInput={(e) => setLatitude((e.target as HTMLInputElement).value)}
                       placeholder="-90 bis 90" />
              </div>
              <div class="hv-modal__field">
                <label>Longitude</label>
                <input type="text" value={longitude}
                       onInput={(e) => setLongitude((e.target as HTMLInputElement).value)}
                       placeholder="-180 bis 180" />
              </div>
              <div class="hv-modal__field">
                <label>Seehöhe (m ü. NN)</label>
                <input type="number" step="1" min="-500" max="9000"
                       value={elevationM !== null ? Math.round(elevationM).toString() : ''}
                       onInput={(e) => {
                         const v = (e.currentTarget as HTMLInputElement).value.trim();
                         if (v === '') { setElevationM(null); return; }
                         const n = Number(v);
                         setElevationM(Number.isFinite(n) ? n : null);
                       }}
                       placeholder="ermittelt der Server beim Speichern"
                       title="Wird beim Speichern als Override an den Server geschickt, sonst backfilled der Terrain-Lookup." />
              </div>
              <div class="hv-plants__form-grid--full" style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <button type="button" class="hv-btn hv-btn--sm"
                        onClick={() => setGeoPickerOpen(true)}
                        title="Position auf einer Karte auswählen">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <IconPickOnMap size={14} />
                    Auf Karte wählen…
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div class="hv-plants__form-section">
            <h3 class="hv-plants__form-section-title">Kontakt</h3>
            <div class="hv-plants__form-grid">
              <div class="hv-modal__field hv-plants__form-grid--full">
                <label>Name</label>
                <input type="text" value={contactName}
                       onInput={(e) => setContactName((e.target as HTMLInputElement).value)}
                       placeholder="z.B. Facility-Manager" />
              </div>
              <div class="hv-modal__field">
                <label>E-Mail</label>
                <input type="email" value={contactEmail}
                       onInput={(e) => setContactEmail((e.target as HTMLInputElement).value)} />
              </div>
              <div class="hv-modal__field">
                <label>Telefon</label>
                <input type="tel" value={contactPhone}
                       onInput={(e) => setContactPhone((e.target as HTMLInputElement).value)} />
              </div>
            </div>
          </div>

          <div class="hv-plants__form-section">
            <h3 class="hv-plants__form-section-title">Field-Clients</h3>
            {clientsState.status === 'loading' && (
              <p class="hv-modal__hint">Lade Client-Liste…</p>
            )}
            {clientsState.status === 'error' && (
              <p class="hv-modal__error">Client-Liste konnte nicht geladen werden.</p>
            )}
            {clientsState.status === 'ok' && clients.length === 0 && (
              <div class="hv-modal__error" style={{ marginBottom: 8 }}>
                <strong>Kein Field-Client registriert.</strong>
                <div style={{ marginTop: 4, fontWeight: 'normal' }}>
                  Wenn du kurz vorher Field-Clients versehentlich gelöscht hast:
                  die MQTT-Retained-Runtime-Info der Clients liegt noch am Broker.
                  Der Reclaim-Button unten versucht per Deterministic-ID-Match aus
                  den Orphan-Records die FieldClient-Zeilen neu anzulegen.
                </div>
                <button type="button" class="hv-btn hv-btn--sm hv-btn--primary"
                        style={{ marginTop: 8 }}
                        onClick={reclaimClients}>
                  FieldClients rekonstruieren
                </button>
              </div>
            )}
            {clientsState.status === 'ok' && clients.length > 0 && (
              <div class="hv-plants__client-list">
                {clients.map((c) => (
                  <ClientRow
                    key={c.id}
                    client={c}
                    product={c.productId ? productById.get(c.productId) ?? null : null}
                    products={products}
                    onPatch={(body, title, message) => patchClient(c.id, body, title, message)}
                    onDelete={() => deleteClient(c.id, c.displayName ?? c.mqttClientName)}
                  />
                ))}
              </div>
            )}
          </div>

          <div class="hv-plants__form-section">
            <h3 class="hv-plants__form-section-title">Notizen</h3>
            <div class="hv-modal__field">
              <label>Operator-Notizen</label>
              <textarea rows={4} value={notes}
                        onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
                        placeholder="Interne Notizen, Zugangsinfos, Besonderheiten..." />
            </div>
          </div>

          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          {confirmDelete ? (
            <>
              <span class="hv-modal__confirm-label">Wirklich löschen? Diese Anlage samt Devices, Nodes und Leaves wird soft-gelöscht.</span>
              <button type="button" class="hv-btn hv-btn--danger" onClick={del} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconDeleteHard size={14} />Ja, löschen</span>
              </button>
              <button type="button" class="hv-btn" onClick={() => setConfirmDelete(false)} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
              </button>
            </>
          ) : (
            <>
              <button type="button" class="hv-btn hv-btn--danger-outline" onClick={() => setConfirmDelete(true)} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconDeleteHard size={14} />Anlage löschen</span>
              </button>
              <div style={{ flex: 1 }} />
              <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
              </button>
              <button type="button" class="hv-btn hv-btn--primary" onClick={save} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconSave size={14} />Speichern</span>
              </button>
            </>
          )}
        </div>
      </div>
      {geoPickerOpen && (
        <GeoPickerModal
          initialLat={parseCoord(latitude)}
          initialLon={parseCoord(longitude)}
          initialAddress={[addressStreet, addressZip, addressCity, addressCountry].filter(Boolean).join(', ')}
          onPick={(lat, lon, pickedElevationM) => {
            setLatitude(lat.toFixed(6));
            setLongitude(lon.toFixed(6));
            if (pickedElevationM !== null) {
              setElevationM(pickedElevationM);
              showToast({
                tone: 'info',
                title: 'Seehöhe erkannt',
                message: `${pickedElevationM.toFixed(0)} m ü. NN — der Server persistiert sie beim Speichern.`,
                autoHideAfterMs: 4000,
              });
            }
          }}
          onClose={() => setGeoPickerOpen(false)}
        />
      )}
    </div>,
    document.body,
  );
}

function parseCoord(raw: string): number | null {
  const n = Number(raw.trim());
  return Number.isFinite(n) && raw.trim() !== '' ? n : null;
}

/** Legacy `hardwareKind` fallback icon — falls verwendet, wenn der Client
 *  noch kein `productId` gesetzt hat. Sonst nimmt <ProductIcon /> das
 *  (osKind, deviceKind)-Paar vom Katalog-Eintrag. */
/** Katalog-DeviceKind auf einen deutschen Formfaktor-Label mappen. */
function formatDeviceKindLabel(v: string | null | undefined): string | null {
  switch (v) {
    case 'gateway': return 'Gateway';
    case 'panel': return 'Touchpanel';
    case 'pc': return 'PC';
    case 'embedded': return 'Embedded';
    default: return null;
  }
}

/** Katalog-OsKind auf Klartext (Linux, Android, …). */
function formatOsKindLabel(v: string | null | undefined): string | null {
  switch (v) {
    case 'linux': return 'Linux';
    case 'android': return 'Android';
    case 'windows': return 'Windows';
    case 'ios': return 'iOS';
    case 'embedded': return 'Embedded';
    default: return null;
  }
}

function renderClientTypeIcon(hardwareKind: string | null) {
  const style = { color: 'var(--text-secondary)', flex: 'none' };
  switch (hardwareKind) {
    case 'Linux-Gateway': return <ClientLinuxGateway size={18} style={style} />;
    case 'Android-Panel': return <ClientAndroidPanel size={18} style={style} />;
    case 'Windows-PC':    return <ClientWindowsPc size={18} style={style} />;
    case 'iOS-Panel':     return <ClientIosPanel size={18} style={style} />;
    case 'Embedded':      return <ClientEmbedded size={18} style={style} />;
    default:              return null;
  }
}

// -----------------------------------------------------------------------------
// Client row — Product-Picker + expandable Instanz-Details. Kept per-row
// component so each row owns its own "instance details expanded" state
// without threading it back through the modal.
// -----------------------------------------------------------------------------

function ClientRow({
  client,
  product,
  products,
  onPatch,
  onDelete,
}: {
  client: FieldClientListItem;
  product: ClientProductSummaryDto | null;
  products: ClientProductSummaryDto[];
  onPatch(body: Record<string, unknown>, title: string, message: string): void;
  onDelete(): void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [serial, setSerial] = useState<string>(client.serialNumber ?? '');
  const [assetTag, setAssetTag] = useState<string>(client.assetTag ?? '');
  const [purchaseDate, setPurchaseDate] = useState<string>(toDateInput(client.purchaseDate));
  const [warrantyUntil, setWarrantyUntil] = useState<string>(toDateInput(client.warrantyUntil));
  const [warrantyRef, setWarrantyRef] = useState<string>(client.warrantyReference ?? '');
  const [warrantyNotes, setWarrantyNotes] = useState<string>(client.warrantyNotes ?? '');
  const [invoiceUrl, setInvoiceUrl] = useState<string>(client.purchaseInvoiceUrl ?? '');

  // Auto-Suggest für Garantie-Ende: purchaseDate + product.standardWarrantyMonths.
  // Nur triggern wenn das Feld leer ist — der Operator hat es evtl. bewusst
  // gesetzt. Kein Autosave; die Suggestion landet nur im Formular-State, der
  // Blur des date-inputs speichert dann.
  useEffect(() => {
    if (warrantyUntil !== '') return;
    if (!product?.standardWarrantyMonths || !purchaseDate) return;
    const suggested = addMonthsToDateInput(purchaseDate, product.standardWarrantyMonths);
    if (suggested) setWarrantyUntil(suggested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseDate, product?.standardWarrantyMonths]);

  const changeProduct = (newProductId: string) => {
    // "" from the picker = "— nicht gesetzt —", explicit clear.
    const body: Record<string, unknown> = { productId: newProductId || null };
    if (!newProductId) body.clearFields = ['productId'];
    const label = newProductId
      ? (products.find((p) => p.id === newProductId)?.displayName ?? 'gewählt')
      : 'nicht gesetzt';
    onPatch(body, 'Produkt zugeordnet', label);
  };

  const commitField = (
    field: string,
    newValue: string,
    previousValue: string,
    date = false,
  ) => {
    if (newValue === previousValue) return;
    const trimmed = newValue.trim();
    const body: Record<string, unknown> = {};
    const clear: string[] = [];
    if (trimmed === '') {
      body[field] = null;
      clear.push(field);
    } else if (date) {
      body[field] = fromDateInput(trimmed);
    } else {
      body[field] = trimmed;
    }
    if (clear.length > 0) body.clearFields = clear;
    onPatch(body, 'Instanz-Feld gespeichert', field);
  };

  // Fallback-Icon: erst Produkt-Icon (via osKind+deviceKind), sonst Legacy
  // hardwareKind-Glyph.
  const icon = product
    ? <ProductIcon osKind={product.osKind} deviceKind={product.deviceKind} size={18} />
    : renderClientTypeIcon(client.hardwareKind);

  // Info-Zeile unter dem Namen: bevorzugt "Gateway · Linux" o.ä. aus Product;
  // wenn kein Product zugewiesen ist, Fallback auf mqttClientName im mono-Look.
  const metaLine = (() => {
    if (!product) return null;
    const form = formatDeviceKindLabel(product.deviceKind);
    const os = formatOsKindLabel(product.osKind);
    const parts = [form, os].filter(Boolean) as string[];
    return parts.length > 0 ? parts.join(' · ') : null;
  })();
  // Displayname des Clients: wenn ein sprechender Name gesetzt ist, nutzen —
  // sonst den mqttClientName. Doppel-Anzeige vermeiden: die Sub-Zeile trägt
  // dann NICHT nochmal die MQTT-Guid, sondern den Formfaktor · OS.
  const clientName = client.displayName ?? client.mqttClientName;

  // Produktbild: wenn das Product einen ProductImageUrl trägt, den nehmen;
  // ansonsten das per Artifact-Id gespeicherte Bild aus dem Store streamen.
  // Beide Wege liefern in derselben <img>-Position — Fallback ohne Bild ist
  // ein leerer linker Bereich, der die aktuelle "nur Name/Formfaktor" Ansicht
  // beibehält.
  const productImageSrc = (() => {
    if (!product) return null;
    if (product.productImageUrl && product.productImageUrl.trim().length > 0) {
      return product.productImageUrl.trim();
    }
    if (product.productImageArtifactId) {
      return `/api/artifacts/${product.productImageArtifactId}/content`;
    }
    return null;
  })();

  return (
    <div class="hv-plants__client-row">
      <div class="hv-plants__client-info">
        {productImageSrc && (
          <img
            src={productImageSrc}
            alt={product?.displayName ?? 'Produktbild'}
            style={{
              // Kompakt-Höhe wenn Instanz-Details ausgeblendet sind (56 px
              // reicht neben der ein- oder zweizeiligen Name-Zeile), voll
              // wenn ausgeklappt — dann darf das Bild den ganzen linken
              // Bereich atmen und dient als Referenz zum Formular rechts.
              maxWidth: '100%',
              maxHeight: expanded ? '220px' : '56px',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              marginBottom: '8px',
              borderRadius: '4px',
              transition: 'max-height 160ms ease',
            }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        <span class="hv-plants__client-name">{clientName}</span>
        {metaLine
          ? <span class="hv-plants__client-mqtt">{metaLine}</span>
          : <span class="hv-plants__client-mqtt mono">{client.mqttClientName}</span>}
      </div>
      <div class="hv-modal__field">
        <label>Produkt</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ color: 'var(--text-secondary)', flex: 'none', display: 'inline-flex' }}>
            {icon}
          </span>
          <select
            value={client.productId ?? ''}
            style={{ flex: '1 1 auto', minWidth: 0 }}
            onChange={(e) => changeProduct((e.currentTarget as HTMLSelectElement).value)}
          >
            <option value="">— nicht gesetzt —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName}
                {p.manufacturerName ? ` — ${p.manufacturerName}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          <button
            type="button"
            class="hv-btn hv-btn--sm"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? 'Instanz-Details ausblenden' : 'Instanz-Details anzeigen'}
          </button>
          <button
            type="button"
            class="hv-btn hv-btn--sm hv-btn--danger-outline"
            title="Client-Zeile hart löschen (Zombie-Aufräumen). Der Client-Runtime-Info + Interfaces gehen mit weg. Kein Undo."
            onClick={onDelete}
          >
            Client entfernen
          </button>
        </div>
        {expanded && (
          <div class="hv-plants__form-grid" style={{ marginTop: 8 }}>
            <div class="hv-modal__field">
              <label>Seriennummer</label>
              <input
                type="text"
                value={serial}
                placeholder="auto-erkannt (Kickstarter)"
                onInput={(e) => setSerial((e.currentTarget as HTMLInputElement).value)}
                onBlur={() => commitField('serialNumber', serial, client.serialNumber ?? '')}
              />
            </div>
            <div class="hv-modal__field">
              <label>Asset-Tag</label>
              <input
                type="text"
                value={assetTag}
                onInput={(e) => setAssetTag((e.currentTarget as HTMLInputElement).value)}
                onBlur={() => commitField('assetTag', assetTag, client.assetTag ?? '')}
              />
            </div>
            <div class="hv-modal__field">
              <label>Kauf-Datum</label>
              <input
                type="date"
                value={purchaseDate}
                onInput={(e) => setPurchaseDate((e.currentTarget as HTMLInputElement).value)}
                onBlur={() => commitField('purchaseDate', purchaseDate, toDateInput(client.purchaseDate), true)}
              />
            </div>
            <div class="hv-modal__field">
              <label>
                Garantie bis
                {product?.standardWarrantyMonths ? (
                  <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>
                    (Std. {product.standardWarrantyMonths} Monate)
                  </span>
                ) : null}
              </label>
              <input
                type="date"
                value={warrantyUntil}
                onInput={(e) => setWarrantyUntil((e.currentTarget as HTMLInputElement).value)}
                onBlur={() => commitField('warrantyUntil', warrantyUntil, toDateInput(client.warrantyUntil), true)}
              />
            </div>
            <div class="hv-modal__field">
              <label>Garantie-Referenz</label>
              <input
                type="text"
                value={warrantyRef}
                onInput={(e) => setWarrantyRef((e.currentTarget as HTMLInputElement).value)}
                onBlur={() => commitField('warrantyReference', warrantyRef, client.warrantyReference ?? '')}
              />
            </div>
            <div class="hv-modal__field hv-plants__form-grid--full">
              <label>Garantie-Notizen</label>
              <textarea
                rows={2}
                value={warrantyNotes}
                onInput={(e) => setWarrantyNotes((e.currentTarget as HTMLTextAreaElement).value)}
                onBlur={() => commitField('warrantyNotes', warrantyNotes, client.warrantyNotes ?? '')}
              />
            </div>
            <div class="hv-modal__field hv-plants__form-grid--full">
              <label>Rechnung-URL</label>
              <input
                type="text"
                value={invoiceUrl}
                onInput={(e) => setInvoiceUrl((e.currentTarget as HTMLInputElement).value)}
                onBlur={() => commitField('purchaseInvoiceUrl', invoiceUrl, client.purchaseInvoiceUrl ?? '')}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
